import { type RegisterInput, registerSchema } from '@discordo/shared';
import { useState, type FormEvent, type ReactElement } from 'react';
import { z } from 'zod';

import { ApiError } from '../lib/api';
import { useAuth } from '../store/auth';

/**
 * Invite-only sign-up screen.
 *
 * The code arrives in the link the inviter shares (`/register?invite=<code>`)
 * and pre-fills the field, but stays editable so a code passed along by hand
 * still works. Validation uses `registerSchema`, the very schema the API parses
 * the body with, so client and server never disagree about what is acceptable.
 *
 * The e-mail is the login identity and has to be one the person can open — a
 * confirmation link goes to it, and reading, writing and voice stay closed
 * until that link is followed. The nickname beside it is only the @mention, and
 * its owner may change it whenever they like.
 */

const INVITE_QUERY_PARAM = 'invite';
const GENERIC_FAILURE = 'Não foi possível criar a conta. Tente de novo.';

type FieldErrors = Partial<Record<keyof RegisterInput, string>>;

function readInviteFromLocation(): string {
  return new URLSearchParams(window.location.search).get(INVITE_QUERY_PARAM) ?? '';
}

export function RegisterPage(): ReactElement {
  const { register } = useAuth();
  const [inviteCode, setInviteCode] = useState(readInviteFromLocation);
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);

    const parsed = registerSchema.safeParse({
      inviteCode,
      email,
      displayName,
      username,
      password,
    });
    if (!parsed.success) {
      const { fieldErrors: issues } = z.flattenError(parsed.error);
      setFieldErrors({
        inviteCode: issues.inviteCode?.[0],
        email: issues.email?.[0],
        displayName: issues.displayName?.[0],
        username: issues.username?.[0],
        password: issues.password?.[0],
      });
      return;
    }

    setFieldErrors({});
    setSubmitting(true);
    try {
      await register(parsed.data);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : GENERIC_FAILURE);
      setSubmitting(false);
    }
  }

  return (
    <main className="auth-screen">
      <form
        className="auth-card"
        noValidate
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        <h1 className="auth-title">Criar conta</h1>
        <p className="auth-subtitle">
          Use o convite que você recebeu para entrar no servidor. Vamos mandar um link de
          confirmação para o seu e-mail.
        </p>

        {error !== null && (
          <p className="auth-alert" role="alert">
            {error}
          </p>
        )}

        <div className="auth-field">
          <label htmlFor="register-invite">Código de convite</label>
          <input
            id="register-invite"
            name="inviteCode"
            type="text"
            value={inviteCode}
            onChange={(event) => setInviteCode(event.target.value)}
          />
          {fieldErrors.inviteCode !== undefined && (
            <p className="auth-field-error">{fieldErrors.inviteCode}</p>
          )}
        </div>

        <div className="auth-field">
          <label htmlFor="register-email">E-mail</label>
          <input
            id="register-email"
            name="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          {fieldErrors.email !== undefined && (
            <p className="auth-field-error">{fieldErrors.email}</p>
          )}
        </div>

        <div className="auth-field">
          <label htmlFor="register-display-name">Nome de exibição</label>
          <input
            id="register-display-name"
            name="displayName"
            type="text"
            autoComplete="nickname"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
          />
          {fieldErrors.displayName !== undefined && (
            <p className="auth-field-error">{fieldErrors.displayName}</p>
          )}
        </div>

        <div className="auth-field">
          <label htmlFor="register-username">Apelido</label>
          <input
            id="register-username"
            name="username"
            type="text"
            autoComplete="username"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
          />
          {fieldErrors.username !== undefined && (
            <p className="auth-field-error">{fieldErrors.username}</p>
          )}
        </div>

        <div className="auth-field">
          <label htmlFor="register-password">Senha</label>
          <input
            id="register-password"
            name="password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          {fieldErrors.password !== undefined && (
            <p className="auth-field-error">{fieldErrors.password}</p>
          )}
        </div>

        <button className="auth-submit" type="submit" disabled={submitting}>
          {submitting ? 'Criando…' : 'Criar conta'}
        </button>

        <p className="auth-footnote">
          Já tem conta? <a href="/login">Entrar</a>
        </p>
      </form>
    </main>
  );
}

export default RegisterPage;
